import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import {
  ZoomIn, ZoomOut, RotateCw, Layers, FileText,
  MousePointer, Maximize2, Image, RefreshCw,
  Hexagon, Tag, Ruler, Grid3X3, ChevronUp, ChevronDown, Move, Merge, MapPin
} from 'lucide-react';
import CanvasEditor from '@/components/CanvasEditor';
import EditorToolbar from '@/components/EditorToolbar';

function MergeOverlapsButton({ onDetectOverlaps }) {
  const [overlaps, setOverlaps] = useState(null);
  const [detecting, setDetecting] = useState(false);

  const handleDetect = async () => {
    setDetecting(true);
    const data = await onDetectOverlaps(true);
    if (data) setOverlaps(data.overlaps);
    setDetecting(false);
  };

  const handleMerge = async () => {
    setDetecting(true);
    await onDetectOverlaps(false);
    setOverlaps(null);
    setDetecting(false);
  };

  return (
    <div className="space-y-1.5 mt-1">
      <button
        onClick={handleDetect}
        disabled={detecting}
        data-testid="detect-overlaps-btn"
        className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded bg-violet-900/30 border border-violet-700/40 text-violet-400 text-[9px] font-heading uppercase tracking-wider hover:bg-violet-900/50 transition-colors disabled:opacity-50"
      >
        <Merge className="w-3 h-3" />
        {detecting ? 'Scanning...' : 'Detect Overlaps'}
      </button>
      {overlaps && overlaps.length > 0 && (
        <button
          onClick={handleMerge}
          disabled={detecting}
          data-testid="merge-overlaps-btn"
          className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded bg-primary/10 border border-primary/30 text-primary text-[9px] font-heading uppercase tracking-wider hover:bg-primary/15 transition-colors disabled:opacity-50"
        >
          <Merge className="w-3 h-3" />
          Merge {overlaps.length} Overlap(s)
        </button>
      )}
    </div>
  );
}

const VERSION_LABELS = {
  original: 'Original',
  enhanced: 'Enhanced',
  annotated: 'Annotated',
  clean: 'Clean',
};

export default function DocumentViewer({
  sheets, selectedSheet, onSelectSheet,
  sheetDetails, sheetImage, imageLoading,
  highlightTag = null,
  imageVersion, onImageVersionChange,
  zoom, onZoomChange,
  editorMode, onModeChange,
  selectedPolygonId, highlightedPolygonIds,
  onPolygonClick, onPolygonHover,
  onCreatePolygon, onUpdatePolygon, onDeletePolygon, onSeedDetect,
  onPublish, onAutoLabel, onCancelDraw, onProcessDocuments,
  canvasLoading, autoLabeling, processingDocs,
  spacesCount = 0,
  spaces = [],
  scrollToPolygon,
  onCleanupSpaces,
  onClassifySpaceTypes,
  labelProgress,
  snapEnabled, onSnapToggle,
  confidenceThreshold, onConfidenceChange, onBulkAccept, pendingBelowCount, pendingAboveCount,
  onDetectOverlaps,
}) {
  const canvasRef = useRef(null);
  const [autoFitDone, setAutoFitDone] = useState(false);
  const [polygonTooltip, setPolygonTooltip] = useState(null);
  const [hudCollapsed, setHudCollapsed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, scrollX: 0, scrollY: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [showPins, setShowPins] = useState(true);
  const [dedupePins, setDedupePins] = useState(false);
  const [hoveredPin, setHoveredPin] = useState(null);

  const roomPolygons = sheetDetails?.room_polygons || [];
  const fixturePins = useMemo(
    () => (sheetDetails?.annotations || []).filter(a => a && a.type === 'equipment' && a.x != null && a.y != null),
    [sheetDetails]
  );
  const imgDimensions = sheetDetails?.dimensions || {};
  const displayPins = useMemo(() => {
    if (!dedupePins) return fixturePins;
    const seen = new Set();
    return fixturePins.filter(p => {
      const k = (p.tag || '').toUpperCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [fixturePins, dedupePins]);
  const imgW = imgDimensions.width || 1600;
  const imgH = imgDimensions.height || 1100;
  const availableVersions = sheetDetails?.available_versions || ['original'];

  // Quick Stats
  const stats = useMemo(() => {
    const total = roomPolygons.length;
    const labeled = roomPolygons.filter(p => p.room_number).length;
    const withArea = roomPolygons.filter(p => p.area_sqft);
    const totalArea = withArea.reduce((sum, p) => sum + (p.area_sqft || 0), 0);
    const labeledPct = total > 0 ? Math.round((labeled / total) * 100) : 0;

    // Split spaces by SF inclusion
    const buildingSF = spaces.filter(s => s.include_in_sf !== false && s.area).reduce((sum, s) => sum + (s.area || 0), 0);
    const nonBuildingSF = spaces.filter(s => s.include_in_sf === false && s.area).reduce((sum, s) => sum + (s.area || 0), 0);

    const totalSF = buildingSF + nonBuildingSF;
    return {
      total, labeled, labeledPct,
      totalArea: Math.round(totalArea),
      buildingSF: Math.round(buildingSF),
      nonBuildingSF: Math.round(nonBuildingSF),
      totalSF: Math.round(totalSF),
      withAreaCount: withArea.length,
    };
  }, [roomPolygons, spaces]);

  // Auto-fit zoom when image dimensions are available
  useEffect(() => {
    if (sheetDetails?.dimensions && canvasRef.current && sheetImage?.image && !autoFitDone) {
      const container = canvasRef.current;
      const containerW = container.clientWidth - 32;
      const containerH = container.clientHeight - 32;
      const fitZoom = Math.min(containerW / imgW, containerH / imgH, 1);
      onZoomChange(Math.max(0.1, Math.round(fitZoom * 100) / 100));
      setAutoFitDone(true);
    }
  }, [sheetDetails?.dimensions, sheetImage, autoFitDone, imgW, imgH, onZoomChange]);

  // Reset auto-fit on sheet change
  useEffect(() => { setAutoFitDone(false); }, [selectedSheet?.id]);

  // Expose scroll-to for parent
  useEffect(() => {
    if (scrollToPolygon && canvasRef.current) {
      const { x, y } = scrollToPolygon;
      const canvas = canvasRef.current;
      const scrollX = (x / 100) * (imgW * zoom) - canvas.clientWidth / 2;
      const scrollY = (y / 100) * (imgH * zoom) - canvas.clientHeight / 2;
      canvas.scrollTo({ left: scrollX, top: scrollY, behavior: 'smooth' });
    }
  }, [scrollToPolygon, imgW, imgH, zoom]);

  // Scroll-to-zoom: mouse wheel on canvas changes zoom centered on cursor
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    // Where the cursor points in document-space before zoom
    const docX = (canvas.scrollLeft + cursorX) / zoom;
    const docY = (canvas.scrollTop + cursorY) / zoom;

    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    const newZoom = Math.max(0.05, Math.min(4, zoom + delta));
    onZoomChange(Math.round(newZoom * 100) / 100);

    // After zoom, scroll so cursor still points at the same document position
    requestAnimationFrame(() => {
      canvas.scrollLeft = docX * newZoom - cursorX;
      canvas.scrollTop = docY * newZoom - cursorY;
    });
  }, [zoom, onZoomChange]);

  // Attach wheel listener with passive:false so preventDefault works
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Space key for pan mode
  useEffect(() => {
    const down = (e) => { if (e.code === 'Space' && !e.repeat) { e.preventDefault(); setSpaceHeld(true); } };
    const up = (e) => { if (e.code === 'Space') { setSpaceHeld(false); setIsPanning(false); } };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Pan handlers — work in pan mode (left-click) or with space+left-click / middle-click in any mode
  const handlePanStart = useCallback((e) => {
    const isPanMode = editorMode === 'pan';
    const isMiddleClick = e.button === 1;
    const isSpaceClick = spaceHeld && e.button === 0;
    if (isPanMode && e.button === 0 || isMiddleClick || isSpaceClick) {
      e.preventDefault();
      setIsPanning(true);
      const canvas = canvasRef.current;
      panStart.current = { x: e.clientX, y: e.clientY, scrollX: canvas.scrollLeft, scrollY: canvas.scrollTop };
    }
  }, [spaceHeld, editorMode]);

  const handlePanMove = useCallback((e) => {
    if (!isPanning) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    canvas.scrollLeft = panStart.current.scrollX - dx;
    canvas.scrollTop = panStart.current.scrollY - dy;
  }, [isPanning]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  const canvasCursor = isPanning ? 'grabbing'
    : (editorMode === 'pan' || spaceHeld) ? 'grab'
    : 'default';

  const handlePolygonHover = (polygon) => {
    onPolygonHover?.(polygon);
    if (!polygon) { setPolygonTooltip(null); return; }
    setPolygonTooltip({
      room: polygon.room_number || `Polygon ${roomPolygons.indexOf(polygon) + 1}`,
      area_sqft: polygon.area_sqft ? `${polygon.area_sqft} SF` : null,
      area: polygon.area_pct ? `${polygon.area_pct}%` : null,
      perimeter_ft: polygon.perimeter_ft ? `${polygon.perimeter_ft} ft` : null,
      sides: polygon.sides,
      centroid: polygon.centroid_pct,
    });
  };

  return (
    <div className="flex-1 min-w-0 bg-card relative border-r border-border flex flex-col overflow-hidden" data-testid="document-viewer">
      {/* Sheet Selector */}
      <div className="h-10 bg-card border-b border-border flex items-center px-3 gap-2 overflow-x-auto shrink-0">
        {sheets.map((sheet) => (
          <button
            key={sheet.id}
            onClick={() => { onSelectSheet(sheet); setAutoFitDone(false); }}
            data-testid={`sheet-tab-${sheet.id}`}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-mono whitespace-nowrap transition-colors ${
              selectedSheet?.id === sheet.id
                ? 'bg-orange-600/10 text-primary border border-primary/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            <FileText className="w-3 h-3" />
            {sheet.sheet_number}
          </button>
        ))}
      </div>

      {/* Version Toggle + Zoom Controls */}
      <div className="h-9 bg-card/80 border-b border-border/50 flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-1">
          {(['original', 'enhanced', 'annotated', 'clean']).map((v) => (
            <button
              key={v}
              onClick={() => onImageVersionChange(v)}
              disabled={!availableVersions.includes(v)}
              data-testid={`version-${v}`}
              className={`px-2 py-1 rounded text-[10px] font-heading uppercase tracking-wider transition-colors ${
                imageVersion === v
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-800'
                  : availableVersions.includes(v)
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'text-muted-foreground cursor-not-allowed'
              }`}
            >
              {VERSION_LABELS[v]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onZoomChange(Math.max(0.1, zoom - 0.1))} className="p-1 hover:bg-secondary rounded text-muted-foreground" data-testid="zoom-out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="font-mono text-[10px] text-muted-foreground w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => onZoomChange(Math.min(4, zoom + 0.1))} className="p-1 hover:bg-secondary rounded text-muted-foreground" data-testid="zoom-in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setAutoFitDone(false)} className="p-1 hover:bg-secondary rounded text-muted-foreground ml-1" data-testid="zoom-fit" title="Fit to view">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onZoomChange(1)} className="p-1 hover:bg-secondary rounded text-muted-foreground" data-testid="zoom-reset" title="100%">
            <RotateCw className="w-3.5 h-3.5" />
          </button>
          {fixturePins.length > 0 && (
            <button
              onClick={() => setShowPins(v => !v)}
              className={`flex items-center gap-1 px-2 py-1 rounded ml-1 text-[10px] font-heading uppercase tracking-wider transition-colors ${showPins ? 'bg-emerald-900/40 border border-emerald-700/50 text-emerald-400' : 'hover:bg-secondary text-muted-foreground border border-transparent'}`}
              data-testid="toggle-fixture-pins"
              title="Show/hide fixture pins"
            >
              <MapPin className="w-3.5 h-3.5" />
              {displayPins.length}
            </button>
          )}
          {showPins && fixturePins.length > 0 && (
            <button
              onClick={() => setDedupePins(v => !v)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-heading uppercase tracking-wider transition-colors ${dedupePins ? 'bg-sky-900/40 border border-sky-700/50 text-sky-400' : 'hover:bg-secondary text-muted-foreground border border-transparent'}`}
              data-testid="toggle-dedupe-pins"
              title={dedupePins ? 'Showing one pin per fixture tag' : 'Showing every tag occurrence'}
            >
              {dedupePins ? 'Unique' : 'All'}
            </button>
          )}
        </div>
      </div>

      {/* Editor Toolbar */}
      <EditorToolbar
        mode={editorMode}
        onModeChange={onModeChange}
        onPublish={onPublish}
        onAutoLabel={onAutoLabel}
        onCancelDraw={onCancelDraw}
        isDrawing={editorMode === 'draw'}
        published={selectedSheet && sheetDetails?.sheet?.published}
        labeling={autoLabeling}
        labelProgress={labelProgress}
        snapEnabled={snapEnabled}
        onSnapToggle={onSnapToggle}
        confidenceThreshold={confidenceThreshold}
        onConfidenceChange={onConfidenceChange}
        onBulkAccept={onBulkAccept}
        pendingBelowCount={pendingBelowCount}
        pendingAboveCount={pendingAboveCount}
      />

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="flex-1 overflow-auto"
        style={{ cursor: canvasCursor }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        data-testid="image-canvas"
      >
        {imageLoading ? (
          <div className="flex flex-col items-center justify-center h-full w-full gap-3">
            <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            <span className="text-xs text-muted-foreground font-mono">Loading sheet...</span>
          </div>
        ) : sheetImage?.image ? (
          <div style={{ display: 'inline-flex', minHeight: '100%', minWidth: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <div
              className="relative shrink-0"
              style={{ width: imgW * zoom, height: imgH * zoom }}
            >
            <img
              src={`data:image/${sheetImage.format || 'png'};base64,${sheetImage.image}`}
              alt={selectedSheet?.sheet_name || 'Sheet'}
              style={{ width: '100%', height: '100%' }}
              className="block select-none"
              draggable={false}
              data-testid="sheet-image"
            />
            <CanvasEditor
              polygons={roomPolygons}
              dimensions={imgDimensions}
              zoom={zoom}
              selectedPolygonId={selectedPolygonId}
              highlightedPolygonIds={highlightedPolygonIds}
              mode={editorMode}
              onPolygonClick={onPolygonClick}
              onPolygonHover={handlePolygonHover}
              onCreatePolygon={onCreatePolygon}
              onUpdatePolygon={onUpdatePolygon}
              onDeletePolygon={onDeletePolygon}
              onSeedDetect={onSeedDetect}
              loading={canvasLoading}
              snapEnabled={snapEnabled}
              confidenceThreshold={confidenceThreshold}
            />
            {showPins && displayPins.length > 0 && (
              <div className="absolute inset-0 pointer-events-none" data-testid="fixture-pin-overlay">
                {displayPins.map((pin, i) => {
                  const hasModel = !!(pin.manufacturer || pin.model);
                  const isHit = highlightTag && pin.tag && pin.tag.toUpperCase() === highlightTag.toUpperCase();
                  const dimmed = highlightTag && !isHit;
                  const base = Math.max(6, 9 * Math.min(zoom, 1.4));
                  return (
                    <div
                      key={i}
                      className="absolute pointer-events-auto cursor-pointer"
                      style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%`, transform: 'translate(-50%, -50%)', opacity: dimmed ? 0.25 : 1, zIndex: isHit ? 5 : 1 }}
                      onMouseEnter={() => setHoveredPin({ ...pin, i })}
                      onMouseLeave={() => setHoveredPin(null)}
                      data-testid={`fixture-pin-${i}`}
                    >
                      <div
                        className={`rounded-full border shadow-md transition-transform hover:scale-150 ${isHit ? 'bg-sky-500 border-white ring-2 ring-sky-300 animate-pulse' : hasModel ? 'bg-emerald-500 border-emerald-200' : 'bg-amber-500 border-amber-200'}`}
                        style={{ width: isHit ? base * 1.8 : base, height: isHit ? base * 1.8 : base }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full w-full gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-900 border border-border flex items-center justify-center">
              <Image className="w-7 h-7 text-muted-foreground" />
            </div>
            <div>
              <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">No Preview Available</p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedSheet ? 'Click "Process Document" to generate processed views' : 'Select a sheet to view'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Polygon tooltip */}
      {polygonTooltip && (
        <div className="absolute bottom-14 left-4 bg-card/95 border border-blue-800/50 rounded-sm px-3 py-2 backdrop-blur-sm z-10" data-testid="polygon-tooltip">
          <div className="flex items-center gap-2 mb-1">
            <MousePointer className="w-3 h-3 text-blue-400" />
            <span className="text-[11px] font-mono font-bold text-foreground">{polygonTooltip.room}</span>
            {polygonTooltip.area_sqft && (
              <span className="text-[10px] font-mono font-bold text-emerald-400">{polygonTooltip.area_sqft}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[9px] font-mono text-muted-foreground">
            {polygonTooltip.area && <span>Area: {polygonTooltip.area}</span>}
            {polygonTooltip.perimeter_ft && <span>Perimeter: {polygonTooltip.perimeter_ft}</span>}
            <span>Sides: {polygonTooltip.sides}</span>
            {polygonTooltip.centroid && <span>@{polygonTooltip.centroid.x}%, {polygonTooltip.centroid.y}%</span>}
          </div>
        </div>
      )}

      {/* Fixture pin tooltip */}
      {hoveredPin && (
        <div className="absolute top-14 right-4 bg-card/95 border border-emerald-800/50 rounded-sm px-3 py-2 backdrop-blur-sm z-20 max-w-[280px]" data-testid="fixture-pin-tooltip">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-3 h-3 text-emerald-400" />
            <span className="text-[12px] font-mono font-bold text-foreground">{hoveredPin.tag || '—'}</span>
            {hoveredPin.description && (
              <span className="text-[10px] text-muted-foreground truncate">{hoveredPin.description}</span>
            )}
          </div>
          <div className="flex flex-col gap-0.5 text-[10px] font-mono text-muted-foreground">
            {(hoveredPin.manufacturer || hoveredPin.model) ? (
              <span className="text-emerald-400">{[hoveredPin.manufacturer, hoveredPin.model].filter(Boolean).join(' · ')}</span>
            ) : (
              <span className="text-amber-400/80">No make/model in schedule</span>
            )}
            {hoveredPin.room_name && (
              <span>Room: {hoveredPin.room_name}{hoveredPin.room_source === 'inherited' ? ' (inherited)' : ''}</span>
            )}
          </div>
        </div>
      )}
      {stats.total > 0 && (
        <div
          className="absolute bottom-4 left-4 z-10 transition-all duration-300"
          data-testid="quick-stats-hud"
        >
          <div className="bg-card/95 border border-border/60 rounded-lg backdrop-blur-xl shadow-2xl shadow-black/40 overflow-hidden"
               style={{ minWidth: hudCollapsed ? 160 : 240 }}>
            {/* HUD Header */}
            <button
              onClick={() => setHudCollapsed(!hudCollapsed)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/40 transition-colors"
              data-testid="hud-toggle"
            >
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
                  Quick Stats
                </span>
              </div>
              {hudCollapsed ? (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-emerald-400 font-bold">{stats.total}</span>
                  <ChevronUp className="w-3 h-3 text-muted-foreground" />
                </div>
              ) : (
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              )}
            </button>

            {/* HUD Body */}
            {!hudCollapsed && (
              <div className="px-3 pb-3 space-y-2.5">
                {/* Polygons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Hexagon className="w-3 h-3 text-blue-400" />
                    <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Polygons</span>
                  </div>
                  <span className="font-mono text-sm text-foreground font-bold" data-testid="hud-polygon-count">{stats.total}</span>
                </div>

                {/* Labeled */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Tag className="w-3 h-3 text-amber-400" />
                      <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Labeled</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs text-foreground" data-testid="hud-labeled-count">{stats.labeled}/{stats.total}</span>
                      <span className={`font-mono text-[10px] font-bold px-1 py-0.5 rounded ${
                        stats.labeledPct >= 80 ? 'text-emerald-400 bg-emerald-950/40'
                        : stats.labeledPct >= 50 ? 'text-amber-400 bg-amber-950/40'
                        : 'text-rose-400 bg-rose-950/40'
                      }`} data-testid="hud-labeled-pct">
                        {stats.labeledPct}%
                      </span>
                    </div>
                  </div>
                  <div className="h-1 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ease-out ${
                        stats.labeledPct >= 80 ? 'bg-emerald-500'
                        : stats.labeledPct >= 50 ? 'bg-amber-500'
                        : 'bg-rose-500'
                      }`}
                      style={{ width: `${stats.labeledPct}%` }}
                    />
                  </div>
                </div>

                {/* Area — split by SF inclusion */}
                {(stats.totalSF > 0 || stats.totalArea > 0) && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Ruler className="w-3 h-3 text-cyan-400" />
                        <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Building SF</span>
                      </div>
                      <span className="font-mono text-xs text-cyan-300 font-bold" data-testid="hud-building-sf">
                        {(stats.buildingSF || stats.totalArea).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Ruler className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Non-Building SF</span>
                      </div>
                      <span className="font-mono text-xs text-muted-foreground" data-testid="hud-non-building-sf">
                        {stats.nonBuildingSF.toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-border/50 pt-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Ruler className="w-3 h-3 text-emerald-400" />
                          <span className="text-[9px] font-heading uppercase tracking-widest text-emerald-500/80">Total SF</span>
                        </div>
                        <span className="font-mono text-sm text-emerald-300 font-bold" data-testid="hud-total-sf">
                          {(stats.totalSF || stats.totalArea).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Spaces */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Grid3X3 className="w-3 h-3 text-primary" />
                    <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Spaces</span>
                  </div>
                  <span className="font-mono text-sm text-orange-300 font-bold" data-testid="hud-spaces-count">{spacesCount}</span>
                </div>

                {/* Cleanup button when spaces > polygons */}
                {spacesCount > stats.total && onCleanupSpaces && (
                  <button
                    onClick={onCleanupSpaces}
                    data-testid="cleanup-spaces-btn"
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 mt-1 rounded bg-amber-900/30 border border-amber-700/40 text-amber-400 text-[9px] font-heading uppercase tracking-wider hover:bg-amber-900/50 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Reconcile ({spacesCount - stats.total} extra)
                  </button>
                )}

                {/* Classify Space Types button */}
                {onClassifySpaceTypes && (
                  <button
                    onClick={onClassifySpaceTypes}
                    data-testid="classify-types-btn"
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 mt-1 rounded bg-sky-900/30 border border-sky-700/40 text-sky-400 text-[9px] font-heading uppercase tracking-wider hover:bg-sky-900/50 transition-colors"
                  >
                    <Tag className="w-3 h-3" />
                    Classify Space Types
                  </button>
                )}

                {/* Auto-Merge Overlaps button */}
                {onDetectOverlaps && (
                  <MergeOverlapsButton onDetectOverlaps={onDetectOverlaps} />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
